import { DynamicDefaultProps, colorToRGBA } from '@synergy/utils';
import { FormattedMessage } from 'react-intl';
import { IndeterminateCheckboxIcon, SelectAllIcon, SelectIcon } from '@synergy/theme';
import { MouseEvent, ReactNode } from 'react';
import {
    Table as MuiTable,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableProps,
    TableRow,
    TableSortLabel,
    Theme,
    Tooltip,
} from '@mui/material';
import { PoolDynamicDefaultPropsComponent } from '@synergy/abstract';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

import { PoolIconToggleButton } from '../icon-toggle-button/pool-icon-toggle-button';
import { PoolTableColumn } from './models/pool-table-column';
import { PoolTablePagination } from '../table-pagination/pool-table-pagination';
import { PoolTablePaginationData } from './models/pool-table-pagination-data';
import { PoolTableRow } from './models/pool-table-row';
import { PoolTableRowValue } from './models/pool-table-row-value';
import { WithRouterProps, withRouter } from '../with-router/with-router';

const styles = (theme: Theme) =>
    createStyles({
        verticalSelectedHeader: {
            borderLeft: `2px solid ${theme.palette.primary.main} !important`,
            borderRight: `2px solid ${theme.palette.primary.main} !important`,
            borderTop: `2px solid ${theme.palette.primary.main} !important`,
            borderRadius: '5px 5px 0 0',
        },
        verticalSelected: {
            borderLeft: `2px solid ${theme.palette.primary.main} !important`,
            borderRight: `2px solid ${theme.palette.primary.main} !important`,
            borderRadius: '0 !important',
        },
        verticalSelectedLast: {
            borderLeft: `2px solid ${theme.palette.primary.main} !important`,
            borderRight: `2px solid ${theme.palette.primary.main} !important`,
            borderBottom: `2px solid ${theme.palette.primary.main} !important`,
            borderRadius: '0 0 5px 5px !important',
        },
        vertical: {
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
            '& thead tr th': {
                border: 'none',
                whiteSpace: 'normal !important',
                margin: 0,
                padding: 0,
            },
            '& thead tr th:first-child': {},
            '& thead tr th:last-child': {
                padding: 0,
            },
            '& tbody': {
                // common table settings
                '& tr': {
                    '& td': {
                        margin: 0,
                        padding: 0,
                    },
                },

                // Borders
                // First row
                '& tr:first-child td': {
                    borderTop: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr:first-child td:first-child': {
                    borderTopLeftRadius: 5,
                },
                '& tr:first-child td:last-child': {
                    borderTopRightRadius: 5,
                    padding: 0,
                },

                // Intermediate rows
                '& tr td': {
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                },
                '& tr td:first-child': {
                    borderLeft: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr td:last-child': {
                    borderRight: `1px solid ${theme.palette.grey[300]}`,
                    padding: 0,
                },

                // Second to last row
                '& tr:nth-last-child(2) td': {
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr:nth-last-child(2) td:first-child': {
                    borderBottomLeftRadius: 5,
                },
                '& tr:nth-last-child(2) td:last-child': {
                    borderBottomRightRadius: 5,
                },

                // Last row
                '& tr:last-child td': {
                    border: 'none',
                },
            },
        },
        default: {
            '& thead th.is-narrow': {
                width: 0,
                whiteSpace: 'nowrap',
            },

            '& thead tr': {
                height: 34,
            },
            '& tr': {
                height: 38,
            },
            '& th': {
                backgroundColor: '#fff',

                '& span': {
                    lineHeight: '14px',
                },
            },

            '& tr td': {
                fontSize: '12.5px',
                lineHeight: '16px',
            },

            '& tr:last-child td': {
                borderBottom: 'none',
            },

            // Pagination
            '& tfoot': {
                background: theme.palette.background.default,
            },
            '& tfoot tr': {
                height: 'unset',
            },
        },
        bordered: {
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& thead tr th': {
                border: 'none',
                whiteSpace: 'nowrap',
            },
            '& tbody': {
                /* First row */
                '& tr:first-child td': {
                    borderTop: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr:first-child td:first-child': {
                    borderTopLeftRadius: 5,
                },
                '& tr:first-child td:last-child': {
                    borderTopRightRadius: 5,
                },

                /* Intermediate row */
                '& tr td:first-child': {
                    borderLeft: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr td:last-child': {
                    borderRight: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr td:nth-child(n+1)': {
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                },

                /* Last row */
                '& tr:last-child td': {
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr:last-child td:first-child': {
                    borderBottomLeftRadius: 5,
                },
                '& tr:last-child td:last-child': {
                    borderBottomRightRadius: 5,
                },
            },
        },
        hover: {
            '& tbody tr': {
                transition: theme.transitions.create('box-shadow', {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.short,
                }),

                '&:hover': {
                    borderRadius: '3px',
                    background: 'white',
                    cursor: 'pointer',
                    boxShadow: `0 10px 24px 0 ${colorToRGBA(theme.palette.text.primary, 0.28)}`,
                    position: 'relative',
                    zIndex: 1,
                },
            },
            '&:hover': {
                overflow: 'visible',
            },
        },
        selectable: {
            borderCollapse: 'separate',
            borderSpacing: 0,
            '& thead tr th': {
                border: 'none',
                whiteSpace: 'nowrap',
            },
            /* Borders and selection-field padding */
            '& tr th:first-child, & tr td:first-child': {
                border: 'none',
                padding: '0 20px 0 12px',
            },
            '& tbody': {
                '& tr': {
                    backgroundColor: theme.palette.background.paper + ' !important',
                },

                /* First row */
                '& tr:first-child td:nth-child(1n+2)': {
                    borderTop: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr:first-child td:nth-child(2)': {
                    borderTopLeftRadius: 5,
                },
                '& tr:first-child td:last-child': {
                    borderTopRightRadius: 5,
                },

                /* Intermediate row */
                '& tr td:nth-child(2)': {
                    borderLeft: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr td:last-child': {
                    borderRight: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr td:nth-child(1n+2)': {
                    borderBottom: `1px solid ${theme.palette.grey[200]}`,
                },

                /* Last row */
                '& tr:last-child td:nth-child(1n+2)': {
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                },
                '& tr:last-child td:nth-child(2)': {
                    borderBottomLeftRadius: 5,
                },
                '& tr:last-child td:last-child': {
                    borderBottomRightRadius: 5,
                },
            },
        },
        disabledSelectButton: {
            opacity: 0.5,
            cursor: 'auto',
        },
        columnHighlight: {
            backgroundColor: theme.palette.grey[100],
        },
        labelPositionFix: {
            position: 'relative',
            top: 1,
        },
        indeterminate: {
            backgroundColor: theme.palette.primary.main,

            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                '& circle': {
                    fill: theme.palette.primary.dark,
                },
            },
        },
        rowDisabled: {
            opacity: 0.5,
        },
        noBorder: {
            '& thead tr th': {
                border: 'none',
            },
            '& tbody tr td': {
                border: 'none',
            },
        },
    });

interface PoolTablePropsBase extends WithStyles<typeof styles>, WithRouterProps {
    columns: PoolTableColumn[];
    rows: PoolTableRow[];
    styleType?: keyof ReturnType<typeof styles>;
    selectable?: boolean;
    selected?: number[];
    selectionDisabled?: number[];
    disabledTooltip?: ReactNode;
    enableHover?: boolean;
    pagination?: PoolTablePaginationData;
    hideHeader?: boolean;
    hideSelectAll?: boolean;
    onSort?: (order: 'asc' | 'desc', sort: string) => void;
    onSelected?: (selected: number[]) => void;
    onRowClicked?: (row: PoolTableRow) => void;
}

type PoolTableProps = TableProps & PoolTablePropsBase;

interface PoolTableState {
    order: 'asc' | 'desc';
    sort: string;
    defaultProps: Partial<PoolTableProps>;
    selected: number[];
    allSelected: boolean;
}

class PoolTableBase extends PoolDynamicDefaultPropsComponent<PoolTableProps, PoolTableState> {
    protected override dynamicDefaultProps: DynamicDefaultProps<PoolTableProps> = {
        default: {
            enableHover: true,
        },
        selectable: {
            selectable: true,
        },
    };

    public override state: PoolTableState = {
        order: (this.props.searchParams.get('order') as 'asc' | 'desc' | null) ?? 'asc',
        sort: this.props.searchParams.get('sort') || '',
        defaultProps: {},
        selected: this.props.selected || [],
        allSelected: false,
    };

    private static getDisplayClass(props: PoolTableProps): string {
        if (props.styleType) {
            return props.classes[props.styleType];
        }

        return props.classes.default;
    }

    private static getClassNames(props: PoolTableProps) {
        const displayClass = this.getDisplayClass(props);
        // Only enable hover for default style if undefined
        const enableHover =
            props.enableHover === undefined ? displayClass === props.classes.default : props.enableHover;
        return `${displayClass} ${enableHover && props.classes.hover} ${props.className || ''}`;
    }

    private handleSort(property: string) {
        const sort = property;
        let order = this.state.order;

        if (this.state.sort === property) {
            order = order === 'asc' ? 'desc' : 'asc';
        }

        this.setState({ order, sort });

        const { onSort } = this.props;

        if (onSort && typeof onSort === 'function') {
            onSort(order, sort);
        }
    }

    private propagateSelected(selected: number[]) {
        if (this.props.onSelected && typeof this.props.onSelected === 'function') {
            this.props.onSelected(selected);
        }
    }

    private handleClick = (id: number) => (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (this.props.selectionDisabled && this.props.selectionDisabled.includes(id)) {
            return;
        }
        const { selected } = this.state;
        const selectedIndex = selected ? selected.indexOf(id) : -1;
        const newSelected: number[] = Array.from(selected);

        if (selectedIndex === -1) {
            newSelected.push(id);
        } else {
            newSelected.splice(selectedIndex, 1);
        }

        this.setState({ selected: newSelected, allSelected: this.allAreSelected(newSelected) });
        this.propagateSelected(newSelected);
    };

    private allAreSelected(selected: number[]) {
        const selectionDisabled = this.props.selectionDisabled || [];
        return this.noneAreSelectable()
            ? false
            : this.props.rows.every(item => selectionDisabled.includes(item.id) || selected.includes(item.id));
    }

    private noneAreSelectable() {
        const selectionDisabled = this.props.selectionDisabled || [];
        return this.props.rows.every(item => selectionDisabled.includes(item.id));
    }

    private handleSelectAllClick = () => {
        const allSelected = !this.state.allSelected;
        const selectionDisabled = this.props.selectionDisabled || [];
        const selectableRows = this.props.rows.filter(row => !selectionDisabled.includes(row.id));
        const selected = allSelected && !this.noneAreSelectable() ? selectableRows.map(row => row.id) : [];
        this.setState({ allSelected, selected });
        this.propagateSelected(selected);
    };

    private createSortHandler(property: string) {
        return () => this.handleSort(property);
    }

    private isSelected = (id: number) => (this.state.selected ? this.state.selected.includes(id) : false);

    private handleRowClicked = (row: PoolTableRow) => () => {
        if (this.props.onRowClicked && typeof this.props.onRowClicked === 'function') {
            this.props.onRowClicked(row);
        }
    };

    private renderTableLabel(column: PoolTableColumn) {
        const shouldRenderSortLabel = column.sortable !== undefined ? column.sortable : true;
        if (shouldRenderSortLabel) {
            return (
                <Tooltip title={<FormattedMessage id="app.common.sort" />} enterDelay={300}>
                    <TableSortLabel
                        active={this.state.sort === column.id}
                        direction={this.state.order}
                        onClick={this.createSortHandler(column.id)}
                    >
                        {column.label || ''}
                        {column.required && ' *'}
                    </TableSortLabel>
                </Tooltip>
            );
        }
        return (
            <span className={this.props.classes.labelPositionFix}>
                {column.label || ''}
                {column.required && ' *'}
            </span>
        );
    }

    private renderTableHead(props: PoolTableProps) {
        const isIndeterminate = this.state.selected.length !== 0 && !this.state.allSelected;

        return (
            <TableHead>
                <TableRow>
                    {props.selectable && (
                        <TableCell padding="checkbox">
                            {!props.hideSelectAll && (
                                <PoolIconToggleButton
                                    selected={this.state.allSelected}
                                    onClick={this.handleSelectAllClick}
                                    className={isIndeterminate ? this.props.classes.indeterminate : ''}
                                >
                                    {isIndeterminate ? (
                                        <IndeterminateCheckboxIcon color="primary" />
                                    ) : (
                                        <SelectAllIcon stroke={this.state.allSelected ? 'white' : undefined} />
                                    )}
                                </PoolIconToggleButton>
                            )}
                        </TableCell>
                    )}
                    {props.columns.map(column => {
                        const highlight =
                            this.props.styleType === 'vertical'
                                ? this.props.classes.verticalSelectedHeader
                                : this.props.classes.columnHighlight;
                        const className = column.highlighted ? highlight : '';

                        return (
                            <TableCell
                                key={column.id}
                                style={{
                                    ...(column.width ? { width: column.width } : {}),
                                    ...(column.textAlign ? { textAlign: column.textAlign } : {}),
                                    whiteSpace: 'nowrap',
                                }}
                                sortDirection={this.state.sort === column.id ? this.state.order : false}
                                className={className}
                            >
                                {this.renderTableLabel(column)}
                            </TableCell>
                        );
                    })}
                </TableRow>
            </TableHead>
        );
    }

    private renderTableBody(props: PoolTableProps) {
        return (
            <TableBody>
                {props.rows.map((row, rowIndex) => {
                    const isSelected = this.isSelected(row.id);
                    const className =
                        row.rowClassName === 'disabled' ? this.props.classes.rowDisabled : row.rowClassName;
                    return (
                        <TableRow
                            role="checkbox"
                            tabIndex={-1}
                            key={row.id}
                            selected={isSelected}
                            onClick={
                                row.rowDisabled
                                    ? undefined
                                    : props.onRowClicked
                                    ? this.handleRowClicked(row)
                                    : this.handleClick(row.id)
                            }
                            className={className}
                        >
                            {props.selectable ? (
                                props.selectionDisabled && props.selectionDisabled.includes(row.id) ? (
                                    <TableCell padding="checkbox">
                                        <Tooltip title={this.props.disabledTooltip ?? ''} placement="top-start">
                                            <div>
                                                <PoolIconToggleButton
                                                    classes={{ root: props.classes.disabledSelectButton }}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <SelectIcon />
                                                </PoolIconToggleButton>
                                            </div>
                                        </Tooltip>
                                    </TableCell>
                                ) : (
                                    <TableCell padding="checkbox">
                                        <PoolIconToggleButton selected={isSelected} onClick={this.handleClick(row.id)}>
                                            <SelectIcon stroke={isSelected ? 'white' : undefined} />
                                        </PoolIconToggleButton>
                                    </TableCell>
                                )
                            ) : null}
                            {this.getColumns(props, row, rowIndex)}
                        </TableRow>
                    );
                })}
            </TableBody>
        );
    }

    private getColumns(props: PoolTableProps, row: PoolTableRow, rowIndex: number) {
        let skip = 0;
        const cols = props.columns.reduce((result, column) => {
            if (skip > 0) {
                skip--;
                return result;
            }

            // calculate style
            const verticalHighlight =
                rowIndex === props.rows.length - 1
                    ? this.props.classes.verticalSelectedLast
                    : this.props.classes.verticalSelected;
            const highlight =
                this.props.styleType === 'vertical' ? verticalHighlight : this.props.classes.columnHighlight;
            const className = row.rowDisabled ? this.props.classes.rowDisabled : column.highlighted ? highlight : '';

            // Set row content
            const rowValue = row[column.id];
            if (rowValue instanceof PoolTableRowValue) {
                const { content, ...rowProps } = rowValue;
                // Set columns to skip after this if colSpan is set.
                skip = rowProps.colSpan ? rowProps.colSpan - 1 : 0;
                result.push(
                    <TableCell
                        key={column.id}
                        style={{
                            ...(column.width ? { width: column.width } : {}),
                            ...(column.textAlign ? { textAlign: column.textAlign } : {}),
                        }}
                        className={className}
                        {...rowProps}
                    >
                        {content}
                    </TableCell>
                );
            } else {
                result.push(
                    <TableCell
                        key={column.id}
                        style={{
                            ...(column.width ? { width: column.width } : {}),
                            ...(column.textAlign ? { textAlign: column.textAlign } : {}),
                        }}
                        className={className}
                    >
                        {rowValue}
                    </TableCell>
                );
            }
            return result;
        }, [] as ReactNode[]);
        return cols;
    }

    private renderFooter(props: PoolTableProps) {
        if (!props.pagination) {
            return null;
        }

        return (
            <TableFooter>
                <TableRow>
                    <PoolTablePagination
                        count={props.pagination.total}
                        page={props.pagination.page}
                        rowsPerPage={props.pagination.limit}
                        rowsPerPageOptions={props.pagination.rowsPerPageOptions}
                        onChangePage={props.pagination.onChangePage}
                        onChangeRowsPerPage={props.pagination.onChangeRowsPerPage}
                    />
                </TableRow>
            </TableFooter>
        );
    }

    public override UNSAFE_componentWillReceiveProps(nextProps: PoolTableProps) {
        if (nextProps.selected) {
            this.setState({
                selected: nextProps.selected,
                allSelected: this.allAreSelected(nextProps.selected),
            });
        }
        if (nextProps.searchParams.get('sort') !== this.props.searchParams.get('sort')) {
            this.setState({
                sort: nextProps.searchParams.get('sort') || '',
                order: (this.props.searchParams.get('order') as 'asc' | 'desc' | null) ?? 'asc',
            });
        }
    }

    public override render() {
        const { defaultProps } = this.state;
        const {
            // Remove not forwarded props
            /* eslint-disable @typescript-eslint/no-unused-vars */
            styleType,
            classes,
            onSort,
            onRowClicked,
            onSelected,
            className,
            disabledTooltip,
            selectionDisabled,
            enableHover,
            hideHeader,
            hideSelectAll,
            selectable,
            /* Router props */
            location,
            params,
            searchParams,
            setSearchParams,
            navigate,
            /* eslint-enable */
            ...baseTableProps
        } = this.props;
        const props = { ...defaultProps, ...this.props };

        return (
            <MuiTable className={PoolTableBase.getClassNames(this.props)} {...baseTableProps}>
                {!hideHeader && this.renderTableHead(props)}
                {this.renderTableBody(props)}
                {this.renderFooter(props)}
            </MuiTable>
        );
    }
}

const poolTableWithRouter = withRouter(PoolTableBase);

export const PoolTable = withStyles(styles)(poolTableWithRouter);
